import {Button, Drawer, Form, Space} from 'antd';
import React, {useEffect} from 'react';
import {useCreateValue, useUpdateValue} from '../../../hooks/parameters';
import {useScaleBands} from '../../../hooks/useScaleBands';
import {useTranslation} from '../../../hooks/useTranslation';
import AssessmentOptionForm from './AssessmentOptionForm';
import {getDisplayText, getValueRowKey, resolveScaleBandOptionId} from './helpers';

interface AssessmentOptionDrawerProps {
  open: boolean;
  record: any | null;
  isParentNode: boolean;
  selectedVariableId: string | number | null;
  selectedVariableGroupId: string | number | null;
  targetName?: string;
  onClose: () => void;
}

const AssessmentOptionDrawer: React.FC<AssessmentOptionDrawerProps> = ({
                                                                         open,
                                                                         record,
                                                                         isParentNode,
                                                                         selectedVariableId,
                                                                         selectedVariableGroupId,
                                                                         targetName,
                                                                         onClose,
                                                                       }) => {
  const {t} = useTranslation();
  const [form] = Form.useForm();
  const {data: scaleBandOptions = []} = useScaleBands();
  const createMutation = useCreateValue();
  const updateMutation = useUpdateValue();

  const isEdit = record != null;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        label: getDisplayText(record),
        scaleBand: resolveScaleBandOptionId(record, scaleBandOptions),
        formula: record.formula || '',
      });
    } else {
      form.setFieldsValue({label: '', scaleBand: undefined, formula: ''});
    }
  }, [open, record, scaleBandOptions, form]);

  const handleSubmit = async () => {
    try {
      const row = await form.validateFields();
      if (isEdit) {
        const updateData: any = {
          label: row.label,
          scaleBandId: row.scaleBand,
        };
        if (isParentNode) {
          updateData.formula = row.formula ?? null;
        }
        await updateMutation.mutateAsync({id: getValueRowKey(record), data: updateData});
      } else {
        const createPayload: any = {
          label: row.label,
          scaleBandId: row.scaleBand,
          ...(isParentNode
            ? {formula: row.formula ?? null, idVariableGroup: selectedVariableGroupId}
            : {idVariable: selectedVariableId}),
        };
        await createMutation.mutateAsync(createPayload);
      }
      onClose();
    } catch (err) {
      console.error('Validation Failed:', err);
    }
  };

  return (
    <Drawer
      title={
        isEdit
          ? t('settings.content.impactAssessment.table.actions.edit')
          : t('settings.content.impactAssessment.table.actions.addValue')
      }
      open={open}
      onClose={onClose}
      size="default"
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>
            {t('settings.content.impactAssessment.table.actions.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {t('settings.content.impactAssessment.table.actions.save')}
          </Button>
        </Space>
      }
    >
      <AssessmentOptionForm
        form={form}
        scaleBandOptions={scaleBandOptions}
        showFormula={isParentNode}
        targetKind={isParentNode ? 'group' : 'variable'}
        targetName={targetName}
      />
    </Drawer>
  );
};

export default AssessmentOptionDrawer;
